import { Check, LoaderCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brand } from '../../components/Brand';
import { errorMessage } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';
import { useAuth } from './authContext';
import { login, logout, requestPasswordReset, setupFirstAdmin, signup } from './authApi';

type LoginMode = 'login' | 'admin' | 'signup';
type Message = { text: string; kind: 'error' | 'success' | 'neutral' } | null;

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  password: '',
  verify: '',
  teamCode: '',
  twoFactor: '',
};

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, needsSetup, loading } = useAuth();
  const [mode, setMode] = useState<LoginMode>('login');
  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState<Message>(null);
  const [working, setWorking] = useState(false);
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotUser, setForgotUser] = useState('');
  const [forgotMessage, setForgotMessage] = useState<Message>(null);
  const [signupComplete, setSignupComplete] = useState(false);

  useEffect(() => {
    if (user) navigate(`/home${user.caps.admin ? '#admin' : ''}`, { replace: true });
  }, [navigate, user]);

  const activeMode = needsSetup ? 'setup' : mode;

  const passwordMatch = useMemo(() => {
    if (activeMode !== 'signup' || !form.verify) return null;
    return form.password === form.verify;
  }, [activeMode, form.password, form.verify]);

  function update(name: keyof typeof emptyForm, value: string) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function chooseMode(next: LoginMode) {
    if (needsSetup) return;
    setMode(next);
    setMessage(next === 'signup'
      ? { text: 'Create your account with your team code - you will be signed in right away.', kind: 'neutral' }
      : null);
    setNeedsTwoFactor(false);
    setForgotOpen(false);
    setForm((current) => ({ ...current, twoFactor: '' }));
  }

  async function finishAuthentication(target: string) {
    await queryClient.invalidateQueries({ queryKey: queryKeys.me });
    navigate(target, { replace: true });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);

    if (activeMode === 'signup') {
      const { firstName, lastName, email, password, verify, teamCode } = form;
      if (!firstName.trim() || !lastName.trim() || !email.trim() || !password) {
        setMessage({ text: 'Please fill in every field.', kind: 'error' });
        return;
      }
      if (!teamCode.trim()) {
        setMessage({ text: 'Enter the team code from your admin.', kind: 'error' });
        return;
      }
      if (password.length < 6) {
        setMessage({ text: 'Password must be at least 6 characters.', kind: 'error' });
        return;
      }
      if (password !== verify) {
        setMessage({ text: 'Passwords do not match.', kind: 'error' });
        return;
      }
      setWorking(true);
      try {
        const result = await signup({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          password,
          code: teamCode.trim(),
        });
        setForm(emptyForm);
        if (result.authed) await finishAuthentication('/home');
        else setSignupComplete(true);
      } catch (error) {
        setMessage({ text: errorMessage(error, 'Sign up failed.'), kind: 'error' });
      } finally {
        setWorking(false);
      }
      return;
    }

    const username = form.username.trim();
    if (!username || !form.password) {
      setMessage({ text: 'Enter your email/username and password.', kind: 'error' });
      return;
    }

    setWorking(true);
    try {
      if (activeMode === 'setup') {
        await setupFirstAdmin({ username, password: form.password });
        await finishAuthentication('/home#admin');
        return;
      }

      const result = await login({ username, password: form.password, code: form.twoFactor.trim() });
      if (result.need2fa) {
        setNeedsTwoFactor(true);
        setMessage({
          text: form.twoFactor.trim()
            ? (result.error || 'Incorrect code.')
            : 'Enter the 6-digit code from your authenticator app.',
          kind: form.twoFactor.trim() ? 'error' : 'neutral',
        });
        return;
      }
      if (activeMode === 'admin' && !result.user?.caps?.admin) {
        await logout();
        setMessage({ text: "That account isn't an admin. Use the Sign in tab.", kind: 'error' });
        return;
      }
      setMessage({ text: 'Success - redirecting...', kind: 'success' });
      await finishAuthentication(activeMode === 'admin' ? '/home#admin' : '/home');
    } catch (error) {
      const payload = error instanceof Error ? error.message : '';
      if (payload.toLowerCase().includes('authenticator')) setNeedsTwoFactor(true);
      setMessage({ text: errorMessage(error, 'Could not reach the server.'), kind: 'error' });
    } finally {
      setWorking(false);
    }
  }

  async function submitReset() {
    if (!forgotUser.trim()) {
      setForgotMessage({ text: 'Enter your username.', kind: 'error' });
      return;
    }
    try {
      await requestPasswordReset(forgotUser.trim());
    } catch {
      // The legacy flow intentionally returns the same message for every outcome.
    }
    setForgotMessage({ text: 'Request sent - your admin will reset your password shortly.', kind: 'success' });
  }

  if (loading || user) return <main className="auth-status"><LoaderCircle className="spin" /><strong>Checking your session</strong></main>;

  const signupMode = activeMode === 'signup';
  const adminMode = activeMode === 'admin';
  const setupMode = activeMode === 'setup';

  return (
    <main className="login-page">
      <section className="login-card" aria-labelledby="login-heading">
        <Brand />
        <p className="login-subtitle" id="login-heading">
          {setupMode ? 'First-time setup' : signupMode ? 'Create an account' : adminMode ? 'Admin sign-in' : 'Sign in to continue'}
        </p>

        {!setupMode ? (
          <div className="auth-tabs" role="tablist" aria-label="Account access">
            <button type="button" role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'active' : ''} onClick={() => chooseMode('login')}>Sign in</button>
            <button type="button" role="tab" aria-selected={adminMode} className={adminMode ? 'active admin' : ''} onClick={() => chooseMode('admin')}>Admin</button>
            <button type="button" role="tab" aria-selected={signupMode} className={signupMode ? 'active' : ''} onClick={() => chooseMode('signup')}>Sign up</button>
          </div>
        ) : (
          <div className="setup-note">No accounts exist yet. Create the first <strong>admin</strong> account - you will use it to add everyone else.</div>
        )}

        <form onSubmit={submit} noValidate>
          {signupMode ? (
            <>
              <Field label="First name" name="firstName" value={form.firstName} onChange={update} autoComplete="given-name" />
              <Field label="Last name" name="lastName" value={form.lastName} onChange={update} autoComplete="family-name" />
              <Field label="Email" hint="(this is your login)" name="email" value={form.email} onChange={update} type="email" autoComplete="email" />
            </>
          ) : (
            <Field label="Email or username" name="username" value={form.username} onChange={update} autoComplete="username" autoFocus />
          )}

          <Field label="Password" name="password" value={form.password} onChange={update} type="password" autoComplete={signupMode || setupMode ? 'new-password' : 'current-password'} />

          {signupMode ? (
            <>
              <Field label="Verify password" name="verify" value={form.verify} onChange={update} type="password" autoComplete="new-password" />
              <Field label="Team code" hint="(from your admin)" name="teamCode" value={form.teamCode} onChange={update} autoComplete="off" placeholder="e.g. 7f3a9c21" />
              <div className={`match-hint ${passwordMatch === true ? 'good' : passwordMatch === false ? 'bad' : ''}`}>
                {passwordMatch === true ? 'Passwords match' : passwordMatch === false ? 'Passwords do not match' : ''}
              </div>
            </>
          ) : null}

          {needsTwoFactor ? (
            <Field label="Authenticator code" name="twoFactor" value={form.twoFactor} onChange={update} inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" maxLength={6} autoFocus />
          ) : null}

          <button className={`primary-button ${adminMode ? 'admin' : ''}`} disabled={working} type="submit">
            {working ? <LoaderCircle className="spin" size={18} /> : null}
            {needsTwoFactor ? 'Verify & sign in' : signupMode ? 'Create account' : setupMode ? 'Create admin & sign in' : adminMode ? 'Sign in as admin' : 'Sign in'}
          </button>
          <StatusMessage message={message} />
        </form>

        {!signupMode && !setupMode ? (
          <div className="forgot-section">
            <button type="button" className="text-button" onClick={() => setForgotOpen((open) => !open)}>Forgot password?</button>
            {forgotOpen ? (
              <div className="forgot-panel">
                <Field label="Your username" name="forgotUser" value={forgotUser} onChange={(_, value) => setForgotUser(value)} />
                <button type="button" className="secondary-button full-width" onClick={submitReset}>Request reset</button>
                <StatusMessage message={forgotMessage} />
                <p>An admin will reset your password and give you a new one. No email is sent.</p>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      {signupComplete ? (
        <div className="dialog-backdrop" role="presentation" onMouseDown={() => { setSignupComplete(false); chooseMode('login'); }}>
          <section className="dialog-card confirmation-card" role="dialog" aria-modal="true" aria-labelledby="signup-complete" onMouseDown={(event) => event.stopPropagation()}>
            <div className="confirmation-icon"><Check size={28} /></div>
            <h2 id="signup-complete">Request submitted!</h2>
            <p>An admin will approve your account - then sign in with your email.</p>
            <button className="primary-button" type="button" onClick={() => { setSignupComplete(false); chooseMode('login'); }}>Got it</button>
          </section>
        </div>
      ) : null}
    </main>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  name: keyof typeof emptyForm | 'forgotUser';
  value: string;
  onChange: (name: keyof typeof emptyForm, value: string) => void;
  type?: string;
  autoComplete?: string;
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'email';
  maxLength?: number;
  autoFocus?: boolean;
}

function Field({ label, hint, name, value, onChange, type = 'text', ...inputProps }: FieldProps) {
  return (
    <label className="field">
      <span>{label} {hint ? <small>{hint}</small> : null}</span>
      <input
        {...inputProps}
        name={name}
        type={type}
        value={value}
        onChange={(event) => onChange(name as keyof typeof emptyForm, event.target.value)}
      />
    </label>
  );
}

function StatusMessage({ message }: { message: Message }) {
  return <div className={`form-message ${message?.kind || ''}`} role={message?.kind === 'error' ? 'alert' : 'status'}>{message?.text || ''}</div>;
}
